
import React from 'react';
import { render_header } from "./common";
import { sprintf } from "sprintf-js";
import {DashboardTable} from "../dashboard-table";
import {centsToString, dollarToString} from "../../../lib/currency-utils";
import {TextLoading} from "../../funcs/utils";
import { Action } from "../../funcs/utils";
import { saveAs } from 'file-saver';
import { AllocationClassification } from "../../../lib/proto/security_classification";
import Portfolio from "../../portfolio/Portfolio";
import {RebalanceTable} from "./rebalance-table";
import {string_cmp} from "../../../lib/js-utils";
import {QueueType} from "../../../lib/proto/queue_proto";
import {create_csv_blob} from "../SAAllocator/csv_util";

const REBALANCE_REASON = {
  REBALANCE_LOW: "Underperformance",
  REBALANCE_HIGH: "Overperformance",
  REBALANCE_CASH: "Cash Reserves",
  REBALANCE_INITIAL: "Initial Allocation",
  REBALANCE_MANUAL: "Manual",
}

const do_download = (component, item, custodian) => () => {

  const date = new Date();

  const filename = sprintf("rebalance_%s_%04d-%02d-%02d.csv",
    item.id, date.getFullYear(), date.getMonth() + 1, date.getDate());

  let account_number = item.metadata.account_number;
  if (custodian === "SCHWAB") {
    account_number = account_number.substring(2);
  }
  const blob = create_csv_blob(item.metadata.trade_orders, account_number);
  saveAs(blob, filename);
}

const dump_multiple = (component, ids, custodian) => () => {
  const queued = component.props.notification.queued;
  const do_csv = component.state.do_csv;
  const rebals = [];
  ids.forEach(id => {
    const rebal = queued.find(item => item.id === id);
    if (rebal && do_csv[rebal.id] === 'yes') {
      rebals.push(rebal);
    }
  });

  const blobs = [];

  rebals.forEach(rebal => {
    let account_number = rebal.metadata.account_number;
    if (custodian === "SCHWAB") {
      account_number = account_number.substring(2);
    }
    blobs.push(create_csv_blob(rebal.metadata.trade_orders, account_number));
  });

  const date = new Date();
  const filename = sprintf("rebalance_%04d-%02d-%02d.csv",
    date.getFullYear(), date.getMonth() + 1, date.getDate());
  saveAs(new Blob(blobs, { type: 'text/plain' }), filename);
}

const render_buttons = (component, item, full_detail) => {

  const rebalance_id = item ? item.id : "all";
  const csv_loading_prop = 'csv_' + rebalance_id;
  const csv_loading = component.state[csv_loading_prop];
  const label = item ? "Download CSV" : "Download All CSV";
  const spacer_style = {
    display: "inline-block",
    width: "2rem",
  }

  const on_click = () => {
    component.props.navigatePath("/super-admin/rebalance?id=" + item.id);
    component.props.loadPortfolio(
      item.username, item.index, component.props.client.chart_colors);
    component.props.loadUserQueueItems(
      item.username, item.index, QueueType.Rebalance.id);

    if (window) {
      window.requestAnimationFrame(() => window.scrollTo(0, 0));
    }
  }

  const wlf = component.props.app.wlf;
  const custodian = wlf.custodian || "SCHWAB";
  const render_detail = !!item && !full_detail;
  const do_multiple = !!component.props.args.ids;
  const cb = do_multiple
    ? dump_multiple(component, component.props.args.ids.split(","), custodian)
    : do_download(component, item, custodian);

  return (
    <div align={"center"} style={{margin: "2rem"}}>
      <div>
        { render_detail && <Action onClick={on_click}>
          Detail
        </Action> }
        { render_detail && <div style={spacer_style}/> }
        <Action onClick={cb}>
          <TextLoading
            label={label}
            loading={csv_loading}/>
        </Action>
      </div>
    </div>
  )
}

const collect_categories = (item, etf_to_category) => {
  const result = new Set();
  const uncategorized_sym = new Set();

  const add_etfs = etfs => {
    Object.keys(etfs).forEach(etf => {
      if (etf !== "CASH" && etf in etf_to_category) {
        result.add(etf_to_category[etf]);
      } else if (etf !== "CASH") {
        uncategorized_sym.add(etf);
      }
    })
  }
  if (item.metadata.current_alloc) {
    add_etfs(item.metadata.current_alloc);
  }

  if (item.metadata.trade_orders) {
    add_etfs(item.metadata.trade_orders);
  }

  if (item.metadata.new_alloc) {
    add_etfs(item.metadata.new_alloc);
  }

  return {
    all_categories: Array.from(result).sort(),
    uncatorized_syms: Array.from(uncategorized_sym).sort(),
  }
}

const dump_categories = (
  all_categories, item, category_to_etfs, uncatorized_syms, irr_scores) => {

  const render_col_headers = title => {
    const title_style = {
      borderBottom: "1px solid black",
      width: "100%",
      fontWeight: "bold",
    }
    return (<div style={title_style}>{title}</div>)
  }
  const is_closed = item.partition.endsWith("CLOSED");
  const prev_portfolio_usd = is_closed ? item.metadata.prev_portfolio_usd : item.metadata.portfolio_usd;
  const is_initial = item.metadata.reason === "REBALANCE_INITIAL";
  const col_titles = [
    "Investment Category",
    "Pct",
    "Symbol",
    "Score",
    "Size", // Current Alloc
    "Current Price",
    "Total Price",
    "Total Pct",
    "Op", // Trade Order
    "Size",
    "Size", // Requested Alloc
    "Total Price",
    "Total Pct"
  ];
  const super_style = {
    fontSize: "1.2rem",
    fontWeight: "bold",
  }
  const border_style = {
    borderLeft: "1px solid black",
    paddingLeft: "0.2rem",
  }

  let total_current_cents = 0.;
  let total_current_pct = 0.;
  let total_requested_cents = 0.;
  let total_requested_pct = 0.;

  const data = [
    [
      "",
      "",
      "",
      "",
      (<div style={super_style}>{ is_closed ? "Previous Allocation" : "Current Allocation" }</div>),
      "",
      "",
      "",
      (<div style={super_style}>Trade Order</div>),
      "",
      (<div style={super_style}>{ is_closed ? "New Allocation" : "Requested Allocation" }</div>),
    ],
    col_titles.map(render_col_headers),
  ]

  const portfolio_usd = item.metadata.portfolio_usd;
  const metadata = item.metadata;

  // Do cash
  let new_alloc_cash_usd = 0.;
  if (metadata.new_alloc) {
    new_alloc_cash_usd = metadata.new_alloc.CASH.price_cents / 100;
  } else {
    console.log(item.id, item.username, item.index, "missing new_alloc");
  }
  const cash_usd = is_closed
    ? metadata.cash.prev_cash
    : metadata.cash_usd;
  const current_cash = dollarToString(cash_usd);
  //const current_casn_pct = sprintf("%.2f %%", cash_usd / portfolio_usd * 100);
  const current_casn_pct = sprintf("%.2f %%", cash_usd / prev_portfolio_usd * 100);
  const new_cash = dollarToString(new_alloc_cash_usd);
  const new_casn_pct = sprintf(
    "%.2f %%", new_alloc_cash_usd / portfolio_usd * 100);

  //total_current_cents += metadata.cash_usd * 100;
  total_current_cents += cash_usd * 100;
  //total_current_pct += metadata.cash_usd / portfolio_usd * 100;
  total_current_pct += cash_usd / portfolio_usd * 100;
  total_requested_cents += new_alloc_cash_usd * 100;
  total_requested_pct += new_alloc_cash_usd / portfolio_usd * 100;

  data.push([
    "Cash",
    "",
    "",
    "",
    (<div style={border_style}>---</div>),
    "---",
    (<div>{is_initial ? "---" : current_cash}</div>),
    (<div>{is_initial ? "---" : current_casn_pct}</div>),
    (<div style={border_style}>---</div>),
    "---",
    (<div style={border_style}>---</div>),
    (<div>{new_cash}</div>),
    (<div>{new_casn_pct}</div>),
  ]);

  const contains = (prop, obj, base, new_prop) => {
    if (!obj) {
      return false;
    }
    return prop in obj;
  }

  all_categories.forEach((category, index) => {
    const etfs_0 = Object.keys(category_to_etfs[category]).sort();

    const etfs = etfs_0.filter(etf =>
      contains(etf, metadata.current_alloc, metadata, "current_alloc")
      || contains(etf, metadata.trade_orders, metadata, "trade_orders")
      || contains(etf, metadata.new_alloc, metadata, "new_alloc"));

    const div_style = {
      backgroundColor: (index + 1) % 2 ? "#EEE" : "#FFF",
    }
    const current_border_style = {
      ...div_style,
      borderLeft: "1px solid black",
      paddingLeft: "0.2rem",
    }
    const BLANK = (override) => {
      const style = {
        ...div_style,
        ...override,
      }
      return (<div style={style}>---</div>);
    }

    etfs.forEach(etf => {
      // Current alloc
      let current_size, current_price, total_price, total_pct;
      if (contains(etf, metadata.current_alloc, item, "current_alloc")) {
        const current = metadata.current_alloc[etf];
        const total_cents = current.price_cents * current.size;
        current_size = (<div style={current_border_style}>{sprintf("%d", current.size)}</div>);
        current_price = (<div style={div_style}>{centsToString(current.price_cents)}</div>);
        total_price = (<div style={div_style}>{centsToString(total_cents)}</div>);
        //total_pct = (<div style={div_style}>{sprintf("%.2f %%", total_cents / portfolio_usd)}</div>);
        total_pct = (<div style={div_style}>{sprintf("%.2f %%", total_cents / prev_portfolio_usd)}</div>);

        total_current_cents += total_cents;
        //total_current_pct += total_cents / portfolio_usd;
        total_current_pct += total_cents / prev_portfolio_usd;

      } else {
        current_size = BLANK(current_border_style);
        current_price = BLANK();
        total_price = BLANK();
        total_pct = BLANK();
      }

      // Trade Op
      let trade_op, trade_size;
      if (contains(etf, metadata.trade_orders, item, "trade_orders")) {
        const current = metadata.trade_orders[etf];
        trade_op = (<div style={current_border_style}>{current['op']}</div>);
        trade_size = (<div style={div_style}>{current['size']}</div>);
      } else {
        trade_op = BLANK(current_border_style);
        trade_size = BLANK();
      }

      const score_str = sprintf("%.4f", irr_scores[etf].score);

      // Requested Alloc
      let requested_size, requested_total_price, requested_pct;
      if (contains(etf, metadata.new_alloc, item, "new_alloc")) {
        const current = metadata.new_alloc[etf];
        const total_cents = current.price_cents * current.size;
        requested_size = (<div style={current_border_style}>{current['size']}</div>);
        requested_total_price = (<div style={div_style}>{centsToString(total_cents)}</div>);
        requested_pct = (<div style={div_style}>{sprintf("%.2f %%", total_cents / portfolio_usd)}</div>);
        total_requested_cents += total_cents;
        total_requested_pct += total_cents / portfolio_usd;
      } else {
        requested_size = BLANK(current_border_style);
        requested_total_price = BLANK();
        requested_pct = BLANK();
      }

      let cat_pct = 0.;
      if (metadata.requested_alloc && metadata.requested_alloc[category]) {
        cat_pct = metadata.requested_alloc[category];
      } else if (metadata.requested_alloc
        && metadata.requested_alloc.alloc
        && metadata.requested_alloc.alloc[category]) {
        cat_pct = metadata.requested_alloc.alloc[category];
      }

      data.push([
        (<div style={div_style}>{AllocationClassification[category].label}</div>),
        (<div style={div_style}>{ sprintf("%.02f%%", cat_pct * 100)}</div>),
        (<div style={div_style}>{etf}</div>),
        (<div style={div_style}>{score_str}</div>),
        is_initial ? BLANK(current_border_style) : current_size,
        is_initial ? BLANK() : current_price,
        is_initial ? BLANK() : total_price,
        is_initial ? BLANK() : total_pct,
        trade_op,
        trade_size,
        requested_size,
        requested_total_price,
        requested_pct,
      ])
    })

  });

  uncatorized_syms.forEach((sym, index) => {
    const div_style = {
      backgroundColor: (all_categories.length + index + 1) % 2 ? "#EEE" : "#FFF",
    }
    const current_border_style = {
      ...div_style,
      borderLeft: "1px solid black",
      paddingLeft: "0.2rem",
    }
    const BLANK = (override) => {
      const style = {
        ...div_style,
        ...override,
      }
      return (<div style={style}>---</div>);
    }
    let total_cents = 0;
    let total_req_cents = 0;

    let current = {
      size: 0,
      price_cents: 0,
    }
    let requested = {
      size: 0,
      price_cents: 0,
    }
    if (sym in metadata.current_alloc) {
      current = metadata.current_alloc[sym];
      total_cents = current.price_cents * current.size;
    }

    total_current_cents += total_cents;
    //total_current_pct += total_cents / portfolio_usd;
    total_current_pct += total_cents / prev_portfolio_usd;

    if (sym in metadata.new_alloc) {
      requested = metadata.new_alloc[sym];
      total_req_cents = requested.price_cents * requested.size;
    }

    total_requested_cents += total_req_cents;
    total_requested_pct += total_req_cents / portfolio_usd;

    // Trade Op
    let trade_op, trade_size;
    if (!metadata.trade_orders) {
      trade_op = BLANK(current_border_style);
      trade_size = BLANK();
    } else {
      if (sym in metadata.trade_orders) {
        const current = metadata.trade_orders[sym];
        trade_op = (<div style={current_border_style}>{current['op']}</div>);
        trade_size = (<div style={div_style}>{current['size']}</div>);
      } else {
        trade_op = BLANK(current_border_style);
        trade_size = BLANK();
      }
    }

    // Requested Alloc
    let requested_size, requested_total_price, requested_pct;
    if (!metadata.new_alloc) {
      requested_size = BLANK(current_border_style);
      requested_total_price = BLANK();
      requested_pct = BLANK();
    } else {
      if (sym in metadata.new_alloc) {
        const current = metadata.new_alloc[sym];
        const total_cents = current.price_cents * current.size;
        requested_size = (<div style={current_border_style}>{current['size']}</div>);
        requested_total_price = (<div style={div_style}>{centsToString(total_cents)}</div>);
        requested_pct = (<div style={div_style}>{sprintf("%.2f %%", total_cents / portfolio_usd)}</div>);
      } else {
        requested_size = BLANK(current_border_style);
        requested_total_price = BLANK();
        requested_pct = BLANK();
      }
    }

    data.push([
      (<div style={div_style}>Unmanaged</div>),
      BLANK(),
      (<div style={div_style}>{sym}</div>),
      BLANK(),
      is_initial ? BLANK(current_border_style) : (<div style={current_border_style}>{sprintf("%d", current.size)}</div>),
      is_initial ? BLANK() : (<div style={div_style}>{centsToString(current.price_cents)}</div>),
      is_initial ? BLANK() :  (<div style={div_style}>{centsToString(total_cents)}</div>),
      is_initial ? BLANK() : (<div style={div_style}>{sprintf("%.2f %%", total_cents / prev_portfolio_usd)}</div>),
      trade_op,
      trade_size,
      requested_size,
      requested_total_price,
      requested_pct,
    ])
  });

  const total_style = {
    borderTop: "1px solid black",
  }
  const BLANK = () => {
    const style = {
      ...total_style,
    }
    return (<div style={style}>---</div>);
  }
  // Push total
  data.push([
    (<div style={total_style}>TOTALS</div>),
    BLANK(),
    BLANK(),
    BLANK(),
    BLANK(),
    BLANK(),
    is_initial ? BLANK() : (<div style={total_style}>{centsToString(total_current_cents)}</div>),
    is_initial ? BLANK() : (<div style={total_style}>{sprintf("%.2f %%", total_current_pct)}</div>),
    BLANK(),
    BLANK(),
    BLANK(),
    (<div style={total_style}>{centsToString(total_requested_cents)}</div>),
    (<div style={total_style}>{sprintf("%.2f %%", total_requested_pct)}</div>),
  ])

  const table_style = {
    marginTop: "1rem",
    width: "100%",
  }
  const col_widths = [ 1.8, 0.4, 0.5, 0.6, 1, 1, 1, 1, 1, 1, 1, 1, 1 ];
  const td_style = {
    paddingRight: "0",
    overflow: "visible',"
  }
  return (<DashboardTable
    tableStyle={table_style}
    col_widths={col_widths}
    td_style={td_style}
    data={data}/>);
}

/**
 * @param date_str YYYY-MM-DD
 * @returns MM-DD-YY
 */
const xlate_date = date_str => {
  if (!date_str) {
    return "---";
  }
  const [ YY, MM, DD ] = date_str.split("-");
  return sprintf("%s-%s-%s", MM, DD, YY);
}

const get_closed_canceled_notifs = (notifications, username, index) => {

  let closed_notifs = [];
  let canceled_notifs = [];

  if (notifications[username] && notifications[username][index]) {
    //return notifications[username][index];
    if (notifications[username][index].closed_notifs) {
      closed_notifs = notifications[username][index].closed_notifs;
    }
    if (notifications[username][index].canceled_notifs) {
      canceled_notifs = notifications[username][index].canceled_notifs;
    }
  }
  return {
    closed_notifs,
    canceled_notifs,
  };
}

const label_value = (label, value) => {
  const div_style = {
    display: "inline",
  }
  const label_style = {
    fontWeight: "bold",
    paddingRight: "1rem",
  }
  return (
    <div style={div_style}>
      <span style={label_style}>{label}</span><span>{value}</span>
    </div>
  )
}

const dump_metadata = notif => {
  const alloc = notif.metadata.requested_alloc;
  if (!alloc) {
    return null;
  }
  const dump_pair = (label_a, value_a, label_b, value_b) => {
    const style = {
      display: "flex",
    }
    const label_style = {
      fontWeight: "bold",
      paddingRight: "1rem",
    }
    return (
      <div style={style}>
        <div style={{flex: "1"}}>
          <span style={label_style}>{label_a}</span>
          <span>{value_a}</span>
        </div>
        <div style={{flex: "1"}}>
          <span style={label_style}>{label_b}</span>
          <span>{value_b}</span>
        </div>
      </div>
    )
  }

  const table_index = 'table_index' in alloc ? alloc.table_index : alloc._table_index;
  const time = 'time' in alloc ? alloc.time : alloc._time;
  const portfolio_years = alloc.portfolio_years || alloc._portfolio_years;
  const num_years = (1.0 - time) * portfolio_years;

  const pseudo = 'pseudo_time' in alloc ? alloc.pseudo_time : alloc._pseudo_time;

  return (
    <div>
      { dump_pair(
        "Table Name:", alloc.table_name || alloc._table_name,
        "Table Index:",  sprintf("%.2f", table_index)
      )}
      { dump_pair(
        "Years to Maturity:", sprintf("%.5f", num_years),
        "Pseudotime:", sprintf("%.5f", pseudo))
      }
    </div>
  )
}

const maybe_dump_cash_metadata = notif => {
  const meta = notif.metadata.cash;
  if(!meta) {
    return null;
  }

  const cash_alloc = sprintf("%.2f%% = %s",
    meta.cash_alloc / meta.account_value * 100,
    dollarToString(meta.cash_alloc));

  return (
    <div style={{ marginTop: "1rem"}}>
      <div>
        Cash Metadata:
      </div>
      <div style={{paddingLeft: "2rem"}}>
        <div>
          { label_value("Current Cash:", dollarToString(meta.current_cash) )}
        </div>
        <div>
          { label_value("Cash Allocation:", cash_alloc) }
        </div>
        <div>
          { label_value("Monthly Fee:", dollarToString(meta.monthly_fee)) }
        </div>
        <div>
          { label_value("Available to Allocate:", dollarToString(meta.cash_available_to_allocate)) }
        </div>
      </div>
    </div>
  )
}

const on_span_selected = component => id => {
  component.setState({
    selected_rebalance: id,
  })
}

const getName = component => {
  let { wlf_id, username, acct_index } = component.props.args;
  acct_index = parseInt(acct_index, 10);
  const wlf_item = component.props.users.wlf[wlf_id];
  if (!wlf_item) {
    return null;
  }
  const item = wlf_item.items.find(item => item.user.username === username && item.acct_index === acct_index);
  if (!item) {
    return null;
  }
  const name = item.user.name;
  return name.first + " " + name.last;
}

const Rebalance = props => {

  const notif = props.notif;
  const metadata = notif.metadata;
  const component = props.component;
  const { category_to_etf, etf_to_category } = component.props.notification;

  if (component.props.irr.loading) {
    return null;
  }

  const { all_categories, uncatorized_syms } = collect_categories(notif, etf_to_category);
  const { closed_notifs, canceled_notifs } = get_closed_canceled_notifs(
    component.props.notification, notif.username, notif.index);

  const irr_scores = component.props.irr.scores[0].SCORES;

  const rebal_reason = metadata.reason in REBALANCE_REASON
    ? REBALANCE_REASON[metadata.reason] : "UNKNOWN";

  const portfolio = props.full_detail
    ? (<div>
      <Portfolio
        on_span_selected={on_span_selected(component)}
        username={notif.username}
        index={notif.index}/>
    </div>)
    : null;

  const rebal_items = [ ...closed_notifs, ...canceled_notifs ];
  rebal_items.sort(
    (item_a, item_b) => string_cmp(item_a.id, item_b.id));

  const rebalance_table = props.full_detail
    ? (<RebalanceTable
      component={component}
      selected={component.state.selected_rebalance}
      state={component.state}
      items={rebal_items}/>)
    : null;

  const current_risk = sprintf("%.2f", metadata.current_risk);
  const new_risk = sprintf("%.2f", metadata.new_risk);
  const name = getName(component);
  const active = component.state.do_csv
    ? component.state.do_csv[notif.id] === 'yes' : true;
  const container_style = {
    color: active ? "#000" : "#BBB",
  }
  return (
    <div style={container_style}>
      { render_header(notif, name, component) }
      <div>
        { label_value("Reason:", rebal_reason) }
      </div>
      <div>
        { label_value("Total Portfolio Value:", dollarToString(metadata.portfolio_usd)) }
      </div>
      <div>
        { label_value("Initial Request:", xlate_date(notif.opened_date)) }
      </div>
      <div>
        { label_value("Last Request:", xlate_date(notif.current_date)) }
      </div>
      <div style={{ display: "flex", marginTop: "1rem"}}>
        <div style={{flex: "1"}}>
          <div>
            { label_value("Current Risk:",  current_risk) }
          </div>
        </div>
        <div style={{flex: "1"}}>
          <div>
            { label_value("New Risk:", new_risk)}
          </div>
         </div>
      </div>
      <div>
        { dump_metadata(notif) }
      </div>
      <div>
        { maybe_dump_cash_metadata(notif) }
      </div>
      <div>
        { dump_categories(
          all_categories, notif, category_to_etf, uncatorized_syms, irr_scores) }
      </div>
      { render_buttons(component, notif, props.full_detail) }
      { portfolio }
      { rebalance_table }
    </div>
  )
}

export { Rebalance, render_buttons };
